import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {TickCircleEmptyIcon, TickCircleFilledIcon} from '../../icons'
import {remCalc} from '../../../theme/_utils'

const FlexibleBundleProgress = ({selectedCount = 0, maxSelectable = 0}) => {
    const intl = useIntl()
    const style = useMultiStyleConfig('Bundle')

    const progressPercentage = useMemo(() => {
        if (!maxSelectable) return 0

        return Math.min((selectedCount / maxSelectable) * 100, 100)
    }, [selectedCount, maxSelectable])

    const progressStatus = useMemo(() => {
        if (selectedCount === 0) return 'empty'
        if (selectedCount >= maxSelectable) return 'complete'
        return 'partial'
    }, [selectedCount, maxSelectable])

    const remainingAccessories = Math.max(maxSelectable - selectedCount, 0)

    /*     const progressIconColor = progressStatus === 'empty' ? '#13183A' : '#EFEDEA' */

    const progressLabel = useMemo(() => {
        if (progressStatus === 'complete') {
            return intl.formatMessage(
                {
                    id: 'flexible_bundle.progress_completed',
                    defaultMessage: 'COMPLETED! {count} ACCESSORIES ADDED',
                },
                {count: maxSelectable},
            )
        }

        if (progressStatus === 'partial') {
            return intl.formatMessage(
                {
                    id: 'flexible_bundle.progress_remaining_partial',
                    defaultMessage:
                        '{count, plural, one {# MORE ACCESSORY TO GO} other {# MORE ACCESSORIES TO GO}}',
                },
                {count: remainingAccessories},
            )
        }

        return intl.formatMessage(
            {
                id: 'flexible_bundle.progress_empty',
                defaultMessage:
                    'ADD {count, plural, one {# ACCESSORY} other {# ACCESSORIES}} OF YOUR CHOICE',
            },
            {count: maxSelectable},
        )
    }, [intl, progressStatus, maxSelectable, remainingAccessories])

    return (
        <Box
            {...style.progressTrack}
            {...style.progressTrackByState?.[progressStatus]}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={maxSelectable}
            aria-valuenow={selectedCount}
        >
            <Box
                {...style.progressFill}
                {...style.progressFillByState?.[progressStatus]}
                width={`${progressPercentage}%`}
            />

            <Flex {...style.progressContent}>
                <Box {...style.progressIcon}>
                    {progressStatus === 'complete' ? (
                        <TickCircleFilledIcon width={remCalc(12)} height={remCalc(12)} />
                    ) : progressStatus === 'partial' ? (
                        <TickCircleEmptyIcon
                            width={remCalc(12)}
                            height={remCalc(12)}
                            color="background.base.default"
                        />
                    ) : (
                        <TickCircleEmptyIcon
                            width={remCalc(12)}
                            height={remCalc(12)}
                            color="foreground.neutral.default"
                        />
                    )}
                </Box>

                <Text {...style.progressText} {...style.progressTextByState?.[progressStatus]}>
                    {progressLabel}
                </Text>
            </Flex>
        </Box>
    )
}

FlexibleBundleProgress.propTypes = {
    selectedCount: PropTypes.number,
    maxSelectable: PropTypes.number,
}

export default FlexibleBundleProgress
